import { FullCLesson } from '@yandex-int/k-common/typings';
import { getDay, format } from 'date-fns';
import { Text } from 'platform-components';
import React from 'react';
import { IntlShape, WrappedComponentProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import ym from 'react-yandex-metrika';
import { Action, bindActionCreators, Dispatch } from 'redux';

import { WEEK_DAYS } from 'common.components/constants';
import { LESSON_DATES } from 'common.components/constants/lesson';
import LessonDateSelect, { Option as LessonDateOption } from 'common.components/lesson-date-select/lesson-date-select';
import * as lessonEditorSelectors from 'common.components/lesson/selectors';
import { getIsWeekend, nextMondayMorning, getIsMondayMorning, getLessonDateTime } from 'utils/lesson';
import { LessonDate } from 'utils/lesson/lesson-time/types';

import * as actions from '../../actions';
import * as selectors from '../../selectors';
import { getDefaultAssignDate, formatDate } from '../../utils';

interface DispatchProps {
    setAssignDate: typeof actions.setAssignDate;
    setDeadlineDate: typeof actions.setDeadlineDate;
}

interface StoreProps {
    assignDate: LessonDate | null;
    deadlineDate: LessonDate | null;
    assignDateError: string | null;
    deadlineDateError: string | null;
    lesson: FullCLesson | null;
    isPreparedLesson: boolean;
}

interface HOCProps extends WrappedComponentProps {}

interface OwnProps {
    disabled: boolean;
    activeSubscriptionSlug: string | null;
}

export interface Props extends OwnProps, StoreProps, DispatchProps, HOCProps {}

export const AssignmentDateTabLesson = (props: Props) => {
    const {
        assignDate,
        deadlineDate,
        assignDateError,
        deadlineDateError,
        intl,
        disabled,
        setAssignDate,
        setDeadlineDate,
        isPreparedLesson,
        lesson,
        activeSubscriptionSlug,
    } = props;
    const now = new Date();

    const handleChangeAssignmentDate = (value: LESSON_DATES, date: Date | null) => {
        let customDate: Date | null = null;

        if (value === LESSON_DATES.CUSTOM) {
            customDate = date ? date : getDefaultAssignDate();
        }

        setAssignDate({ type: value, customDate });

        const params = {
            clessonId: lesson?.clesson?.id,
            lessonId: lesson?.id,
            contentType: isPreparedLesson ? 'preparedLesson' : 'courseLesson',
            subscription_slug: activeSubscriptionSlug,
        };

        if (value === LESSON_DATES.CUSTOM) {
            ym('reachGoal', 'teacher_lesson_creation_set_time_calendar', params);
        } else {
            ym('reachGoal', 'teacher_lesson_creation_set_time', {
                optionNumber: getAssignDateOptions(intl, now).findIndex((item) => item.value === value),
                ...params,
            });
        }
    };

    const handleChangeDeadlineDate = (value: LESSON_DATES, date: Date | null) => {
        let customDate: Date | null = null;

        if (value === LESSON_DATES.CUSTOM) {
            if (date) {
                customDate = date;
            } else {
                const defaultSelectedOption = getDeadlineDateOptions(intl, now)[0];
                customDate = getLessonDateTime(now, defaultSelectedOption.value);
            }
        }

        setDeadlineDate({ type: value, customDate });

        const params = {
            clessonId: lesson?.clesson?.id,
            lessonId: lesson?.id,
            contentType: isPreparedLesson ? 'preparedLesson' : 'courseLesson',
            subscription_slug: activeSubscriptionSlug,
        };

        if (value === LESSON_DATES.CUSTOM) {
            ym('reachGoal', 'lesson_deadline_calendar', params);
        } else {
            ym('reachGoal', 'lesson_deadline_predefined', {
                // TODO: вынести в утилиту
                optionNumber: getDeadlineDateOptions(intl, now).findIndex((item) => item.value === value),
                ...params,
            });
        }
    };

    const assignDateDivider = intl.formatMessage({ id: 'assignLessonModal.lessonDatePicker.fromDivider' });
    const deadlineDateDivider = intl.formatMessage({ id: 'assignLessonModal.lessonDatePicker.toDivider' });

    return (
        <div className="assignment__dates-wrapper">
            <div className="assignment__start-date">
                <div className="assignment__label">
                    <Text id="assignLessonModal.studentsStartsWith" />
                </div>
                <LessonDateSelect
                    customDate={assignDate?.customDate}
                    disabled={disabled}
                    errorMessage={assignDateError}
                    formatDate={(date: Date) => formatDate(date, assignDateDivider)}
                    mix={assignDate ? `qa_assign-date_${assignDate.type}` : undefined}
                    onChange={handleChangeAssignmentDate}
                    options={getAssignDateOptions(intl, now)}
                    preventCrossing
                    value={assignDate?.type}
                />
            </div>
            <div className="assignment__deadline-date">
                <div className="assignment__label">
                    <Text id="assignLessonModal.studentsFinishBefore" />
                </div>
                <LessonDateSelect
                    customDate={deadlineDate?.customDate}
                    disabled={disabled}
                    errorMessage={deadlineDateError}
                    formatDate={(date: Date) => formatDate(date, deadlineDateDivider)}
                    mix={deadlineDate ? `qa_deadline-date_${deadlineDate.type}` : undefined}
                    onChange={handleChangeDeadlineDate}
                    options={getDeadlineDateOptions(intl, now)}
                    preventCrossing
                    value={deadlineDate?.type}
                />
            </div>
        </div>
    );
};

const mapStateToProps = (storeState: any): StoreProps => ({
    assignDate: selectors.getAssignDate(storeState),
    deadlineDate: selectors.getDeadlineDate(storeState),
    assignDateError: selectors.getAssignDateError(storeState),
    deadlineDateError: selectors.getDeadlineDateError(storeState),
    lesson: lessonEditorSelectors.getLesson(storeState),
    isPreparedLesson: lessonEditorSelectors.getIsPreparedLesson(storeState),
});

const mapDispatchToProps = (dispatch: Dispatch<Action>): DispatchProps => {
    return bindActionCreators(
        {
            setAssignDate: actions.setAssignDate,
            setDeadlineDate: actions.setDeadlineDate,
        },
        dispatch
    );
};

export const getAssignDateOptions = (intl: IntlShape, now: Date): LessonDateOption[] => {
    const options: LessonDateOption[] = [];

    options.push({
        value: LESSON_DATES.AFTER_ASSIGN,
        label: intl.formatMessage({ id: 'assignLessonModal.datePresets.afterAssign' }),
    });

    if (getIsWeekend(now)) {
        options.push({
            value: LESSON_DATES.FRIDAY_AFTERNOON,
            label: intl.formatMessage({ id: 'assignLessonModal.datePresets.nextFridayAfternoon' }),
        });
    } else if (getDay(now) === WEEK_DAYS.FRIDAY) {
        options.push({
            value: LESSON_DATES.TODAY_AFTERNOON,
            label: intl.formatMessage({ id: 'assignLessonModal.datePresets.todayAfternoon' }),
        });
    } else {
        options.push({
            value: LESSON_DATES.FRIDAY_AFTERNOON,
            label: intl.formatMessage({ id: 'assignLessonModal.datePresets.fridayAfternoon' }),
        });
    }

    return options;
};

export const getDeadlineDateOptions = (intl: IntlShape, now: Date): LessonDateOption[] => {
    const options: LessonDateOption[] = [];

    if (getIsMondayMorning(now)) {
        const label = intl.formatMessage({ id: 'assignLessonModal.datePresets.todayMorning' });
        options.push({
            value: LESSON_DATES.TODAY_MORNING,
            label: label,
        });
    } else {
        const label = intl.formatMessage({ id: 'assignLessonModal.datePresets.mondayMorning' });
        options.push({
            value: LESSON_DATES.NEXT_WEEK_MONDAY,
            label: label,
        });
    }

    if (getIsWeekend(now)) {
        const nextMondayDateTime = nextMondayMorning(now);
        const values = { date: format(nextMondayDateTime, 'dd.MM') };
        const label = intl.formatMessage({ id: 'assignLessonModal.datePresets.mondayWithDate' }, values);

        options.push({
            value: LESSON_DATES.MONDAY_IN_TWO_WEEKS,
            label: label,
        });
    }

    options.push({
        value: LESSON_DATES.NONE,
        label: intl.formatMessage({ id: 'assignLessonModal.datePresets.withoutDeadline' }),
    });

    return options;
};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(AssignmentDateTabLesson));
